import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { setDoc, doc, query, collection, where, getDocs } from 'firebase/firestore';
import { TextField, Button, Typography, Link, Box } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import SnackbarAlert from '../SnackbarAlert/SnackbarAlert';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import useSnackbar from '../../hooks/useSnackbar';
import useResponsive from '../../hooks/useResponsive';
import { validateUsername, validateEmail, validatePassword } from '../../utils/validation';
import { logError, getUserErrorMessage } from '../../utils/security';
import { authRateLimiter, checkRateLimit } from '../../utils/rateLimiter';
import {
  Wrapper,
  StyledPaper,
  FormBox,
  SubmitButton,
  ForgotPasswordButton,
  LanguageSwitcherContainer
} from './Login.styles';

function Login() {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [username, setUsername] = useState('');
  const { openSnackbar, snackbarMessage, snackbarSeverity, showAlert, closeSnackbar } = useSnackbar();

  const translateValidationError = (result) => (result.errorKey ? t(result.errorKey, result.errorParams) : result.error);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Rate limiting check
      checkRateLimit(authRateLimiter, email, 'authentication');
      
      // Validate email
      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        showAlert(translateValidationError(emailValidation), 'error');
        return;
      }
      
      // Validate password
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        // Give users guidance during signup; stay generic on signin
        if (isSignUp) {
          showAlert(translateValidationError(passwordValidation), 'error');
        } else {
          showAlert(t('genericError'), 'error');
        }
        return;
      }

      if (isSignUp) {
        // Validate username for signup
        const usernameValidation = validateUsername(username);
        if (!usernameValidation.valid) {
          showAlert(translateValidationError(usernameValidation), 'error');
          return;
        }

        // Check if username already exists
        const usernameQuery = query(
          collection(db, 'publicProfiles'),
          where('username', '==', usernameValidation.sanitized)
        );
        const usernameSnapshot = await getDocs(usernameQuery);

        if (!usernameSnapshot.empty) {
          showAlert(t('usernameTaken'), 'error');
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, emailValidation.sanitized, password);
        const user = userCredential.user;

        // Store sanitized username in Firestore
        await Promise.all([
          setDoc(doc(db, 'users', user.uid), { username: usernameValidation.sanitized }),
          setDoc(doc(db, 'publicProfiles', user.uid), { username: usernameValidation.sanitized })
        ]);

        // Navigate to dashboard after successful signup
        navigate('/');
      } else {
        await signInWithEmailAndPassword(auth, emailValidation.sanitized, password);
        // Navigate to dashboard after successful login
        navigate('/');
      }
    } catch (error) {
      logError(error, isSignUp ? 'SIGNUP' : 'SIGNIN');
      showAlert(getUserErrorMessage(error, {
        invalidEmail: t('invalidEmail'),
        // Keep credential errors generic to avoid hinting at validity
        invalidCredential: t('genericError'),
        // Keep password errors generic to avoid hinting at policy
        weakPassword: isSignUp ? t('weakPassword') : t('genericError'),
        emailInUse: t('emailInUse'),
        tooManyRequests: t('tooManyRequests'),
        genericError: t('genericError'),
      }), 'error');
    }
  };

  const handlePasswordReset = async () => {
    try {
      // Rate limiting check
      checkRateLimit(authRateLimiter, email, 'password reset');
      
      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        showAlert(translateValidationError(emailValidation), 'warning');
        return;
      }
      
      await sendPasswordResetEmail(auth, emailValidation.sanitized);
      showAlert(t('resetLinkSent'), 'info');
    } catch (error) {
      logError(error, 'PASSWORD_RESET');
      showAlert(getUserErrorMessage(error, {
        resetInvalidEmail: t('resetInvalidEmail'),
        tooManyRequests: t('tooManyRequests'),
        genericError: t('genericError'),
      }), 'error');
    }
  };

  return (
    <> 
      <Wrapper isMobile={isMobile}>
        {isMobile && (
          <LanguageSwitcherContainer>
            <LanguageSwitcher />
          </LanguageSwitcherContainer>
        )}
        <StyledPaper elevation={3} isMobile={isMobile}>
          {!isMobile && (
            <LanguageSwitcherContainer>
              <LanguageSwitcher />
            </LanguageSwitcherContainer>
          )}
           <Typography component="h1" variant="h5">
             {isSignUp ? t('signUp') : t('signIn')}
           </Typography>
          <FormBox component="form" onSubmit={handleSubmit}>
             <TextField
               margin="normal"
               required
               fullWidth
               id="email"
               label={t('emailAddress')}
               name="email"
               autoComplete="email"
               autoFocus
               value={email}
               onChange={(e) => setEmail(e.target.value)}
            />
            {isSignUp && (
              <TextField
                margin="normal"
                required
                fullWidth
                name="username"
                label={t('username')}
                type="text"
                id="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                slotProps={{ pattern: '^[^@]+$'}}
              />
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <SubmitButton
              type="submit"
              fullWidth
              variant="contained"
            >
              {isSignUp ? t('signUp') : t('signIn')}
            </SubmitButton>
            <Button
               fullWidth
               variant="text"
               onClick={() => setIsSignUp(!isSignUp)}
            >
               {isSignUp
                ? t('alreadyRegistered')
                : t('noAccount')}
            </Button>
            {!isSignUp && (
              <ForgotPasswordButton
                fullWidth
                variant="text"
                onClick={handlePasswordReset}
              >
                {t('forgotPassword')}
              </ForgotPasswordButton>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
              <Link
                component={RouterLink}
                to="/privacy"
                underline="always"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                {t('privacyPolicyTitle')}
              </Link>

              <Link
                component={RouterLink}
                to="/terms"
                underline="always"
                color="text.secondary"
                sx={{ fontSize: '0.75rem' }}
              >
                {t('termsOfServiceTitle')}
              </Link>
            </Box>
          </FormBox>
        </StyledPaper>
      </Wrapper>
       <SnackbarAlert
         open={openSnackbar}
         message={snackbarMessage}
         severity={snackbarSeverity}
         onClose={closeSnackbar}
      />
      </>
  );
}

export default Login;
